import type { Dispatch, FC, SetStateAction } from "react"
import { Fragment, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"
import type { EdgeProps } from "reactflow"
import { EdgeLabelRenderer, getBezierPath, useStoreApi } from "reactflow"

import AddIcon from "@mui/icons-material/Add"
import type { PopoverVirtualElement } from "@mui/material"
import { Box, Menu, MenuItem } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { getTheme } from "../../../../hooks/UseTheme"
import { KonanAPI } from "../../../../services/KonanAPI"
import type { PaginatedCalculatorListList } from "../../../../types/generated/api/PaginatedCalculatorListList"
import type { PaginatedDeploymentListList } from "../../../../types/generated/api/PaginatedDeploymentListList"
import type { PaginatedProgramList } from "../../../../types/generated/api/PaginatedProgramList"
import type { PaginatedRuleSetListList } from "../../../../types/generated/api/PaginatedRuleSetListList"
import type { PaginatedScoreCardSetListList } from "../../../../types/generated/api/PaginatedScoreCardSetListList"
import type { PaginatedScriptGroupListList } from "../../../../types/generated/api/PaginatedScriptGroupListList"
import type { PaginatedTagListListList } from "../../../../types/generated/api/PaginatedTagListListList"
import { RuleSetCreateRequest } from "../../../../types/generated/api/RuleSetCreateRequest"
import { getEnvVar, isEnvVariableTruthy } from "../../../../utils/genericHelpers"
import { NODES_WITHOUT_LABEL } from "../../fixtures/workflow-fixtures"
import type { UpdateGraphParams } from "../../types/interfaces"
import { isNodeInFilter, isNodeInLoop, nodeBuilder } from "../../utils/workflowHelpers"
import { CalculatorNode } from "../nodes/CalculatorNode"
import { FilterNode } from "../nodes/FilterNode"
import { LoopStartNode } from "../nodes/LoopStartNode"
import { ProgramNode } from "../nodes/ProgramNode"
import { ProjectNode } from "../nodes/ProjectNode"
import { RulesetNode } from "../nodes/RulesetNode"
import { ScorecardsetNode } from "../nodes/ScorecardsetNode"
import { ScriptNode } from "../nodes/ScriptNode"
import { TaglistNode } from "../nodes/TaglistNode"
import { MenuItemWithTooltip } from "../nodes/base/AddBlockNode"
import { LabeledNode } from "../nodes/base/LabeledNode"

import styles from "../nodes/Nodes.module.scss"

type NodeOptionsProps = {
  anchorEl: Element | (() => Element) | PopoverVirtualElement | (() => PopoverVirtualElement) | null | undefined
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | undefined | null>>
  source: string
  updateGraph: (data: UpdateGraphParams) => void
  target: string
  edgeLabel?: string
}

/**
 * NodeOptionsMenu component that provides a context menu for adding nodes between existing nodes.
 * Displays available node types with tooltips and handles node creation logic.
 */
function NodeOptionsMenu({
  anchorEl,
  setAnchorEl,
  source,
  updateGraph,
  target,
  edgeLabel,
}: Readonly<NodeOptionsProps>): React.ReactElement {
  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // Theme hook
  const theme = getTheme()

  // React Flow hooks
  const store = useStoreApi()
  const { getNodes } = store.getState()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "nodeOptionsMenu.menuTooltips",
  })

  // Constants
  const open = Boolean(anchorEl)
  const page = 1
  const pageSize = 1

  // Data fetching queries
  // Fetch rulesets
  const { data: rulesets, isLoading: isRulesetsLoading } = useQuery<
    AxiosResponse<PaginatedRuleSetListList>,
    AxiosError
  >(["rulesets", projectId, page, pageSize], () => KonanAPI.fetchRulesets(projectId as string, page, pageSize), {
    enabled: !!projectId && open,
  })

  // Fetch taglists
  const { isLoading: isTaglistsLoading, data: taglists } = useQuery<
    AxiosResponse<PaginatedTagListListList>,
    AxiosError
  >(["taglists", projectId, page, pageSize], () => KonanAPI.fetchTagLists(projectId as string, page, pageSize), {
    enabled: !!projectId && open,
  })

  // Fetch scorecardsets
  const { isLoading: isScorecardsetsLoading, data: scorecardsets } = useQuery<
    AxiosResponse<PaginatedScoreCardSetListList>,
    AxiosError
  >(
    ["scorecardsets", projectId, page, pageSize],
    () => KonanAPI.fetchScorecardsets({ projectId: projectId as string, page, pageSize }),
    {
      enabled: !!projectId && open,
    },
  )

  // Fetch programs
  const { isLoading: isProgramsLoading, data: programs } = useQuery<AxiosResponse<PaginatedProgramList>, AxiosError>(
    ["programs", projectId, page, pageSize],
    () => KonanAPI.fetchPrograms(projectId as string, page, pageSize),
    {
      enabled: !!projectId && open,
    },
  )

  // Fetch calculators
  const { isLoading: isCalculatorsLoading, data: calculators } = useQuery<
    AxiosResponse<PaginatedCalculatorListList>,
    AxiosError
  >(
    ["calculators", projectId, page, pageSize],
    () => KonanAPI.listCalculators({ projectId: projectId as string, page, page_size: pageSize }),
    {
      enabled: !!projectId && open,
    },
  )

  // Fetch scripts
  const { data: scripts, isLoading: isScriptsLoading } = useQuery<
    AxiosResponse<PaginatedScriptGroupListList>,
    AxiosError
  >(["scripts", projectId, page, pageSize], () => KonanAPI.fetchScripts(projectId as string, page, pageSize), {
    enabled: !!projectId && open,
  })

  // Fetching projects
  const { isLoading: isProjectsLoading, data: projects } = useQuery<
    AxiosResponse<PaginatedDeploymentListList>,
    AxiosError
  >(
    ["projects", page, pageSize],
    () =>
      KonanAPI.fetchProjects({
        page,
        pageSize,
        category: "model",
        type: ["classification", "credit_scoring"],
      }),
    {
      enabled: open,
    },
  )

  // Memoized values
  // Extracting and memoizing the generic typed rulesets
  const genericRulesetsMemo = useMemo(() => {
    const generics = rulesets?.data?.results?.filter((ruleset) => ruleset?.type === RuleSetCreateRequest.type.GENERIC)
    return generics
  }, [rulesets?.data?.results])

  // Helper functions
  const validateAddingLabelNode = (): boolean => {
    const targetNode = getNodes().find((node) => node.id === target)

    if (targetNode) {
      return targetNode?.data?.nodeType !== "LoopEndNode"
    }

    return false
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  /**
   * Handler for selecting an item (node) to add between existing nodes.
   * Determines routing logic and creates appropriate nodes based on the selected type.
   */
  const handleItemClick = (type: string, optionalProps?: Record<string, unknown>): void => {
    const nodes = getNodes()
    const isSelectedNodeInLoop = isNodeInLoop(source, nodes)

    const isSelectedNodeInFilter = isNodeInFilter(source, nodes)

    const newNodes = nodeBuilder(
      source,
      type,
      updateGraph,
      {
        isNodeInLoop: isSelectedNodeInLoop.isNodeInLoop,
        isNodeInNestedLoop:
          isSelectedNodeInLoop.isNodeInNestedLoop || (isSelectedNodeInLoop.isNodeInLoop && type === "LoopStartNode"),
        loopId: isSelectedNodeInLoop.loopId,
      },
      {
        filterBranch: ["true", "false"].includes(edgeLabel as string)
          ? (edgeLabel as string)
          : (isSelectedNodeInFilter.filterBranch as string),
        isNodeInFilter: isSelectedNodeInFilter.isNodeInFilter,
        filterId: isSelectedNodeInFilter.filterId,
      },
      optionalProps,
    )

    if (updateGraph) {
      updateGraph({
        arrayOfNewNodes: newNodes,
        isNodeBetween: true,
        targetNode: target,
        sourceNode: source,
        edgeLabel,
      })
    }
  }

  return (
    <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      MenuListProps={{
        "aria-labelledby": "basic-button",
      }}
      classes={{
        paper: styles.menu,
      }}
      slotProps={{
        paper: {
          style: {
            backgroundColor: theme.palette.base.background[1],
            border: theme.palette.base.background.enabled,
          },
        },
      }}
      variant="selectedMenu"
    >
      {/* Ruleset node */}
      <MenuItemWithTooltip
        Item={
          <RulesetNode
            menuView
            data={{ nodeType: "RulesetNode", isNodeInLoop: isNodeInLoop(source, getNodes()).isNodeInLoop }}
          />
        }
        onSelectingBlock={() => handleItemClick("RulesetNode")}
        tooltipCondition={!isRulesetsLoading && genericRulesetsMemo?.length === 0}
        tooltipText={t("rulesetNode")}
        isNodeDisabled={isRulesetsLoading}
      />

      <Box mt={1} />
      <MenuItem onClick={() => handleItemClick("DecisionNode")}>
        <RulesetNode menuView data={{ nodeType: "DecisionNode" }} />
      </MenuItem>

      <Box mt={1} />
      {/* TaglistNode node */}
      <MenuItemWithTooltip
        Item={<TaglistNode menuView data={{ nodeType: "TaglistNode" }} />}
        onSelectingBlock={() => handleItemClick("TaglistNode")}
        tooltipCondition={!isTaglistsLoading && taglists?.data?.results?.length === 0}
        tooltipText={t("taglistNode")}
        isNodeDisabled={isTaglistsLoading}
      />

      <Box mt={1} />
      {/* Scorecardset node */}
      <MenuItemWithTooltip
        Item={<ScorecardsetNode menuView data={{ nodeType: "ScorecardsetNode" }} />}
        onSelectingBlock={() => handleItemClick("ScorecardsetNode")}
        tooltipCondition={!isScorecardsetsLoading && scorecardsets?.data?.results?.length === 0}
        tooltipText={t("scorecardsetNode")}
        isNodeDisabled={isScorecardsetsLoading}
      />

      <Box mt={1} />
      {/* Program node */}
      <MenuItemWithTooltip
        Item={<ProgramNode menuView data={{ nodeType: "ProgramNode" }} />}
        onSelectingBlock={() => handleItemClick("ProgramNode")}
        tooltipCondition={!isProgramsLoading && programs?.data?.results?.length === 0}
        tooltipText={t("programNode")}
        isNodeDisabled={isProgramsLoading}
      />

      <Box mt={1} />
      <MenuItem onClick={() => handleItemClick("FilterNode")}>
        <FilterNode menuView />
      </MenuItem>

      <Box mt={1} />
      <MenuItemWithTooltip
        Item={<CalculatorNode menuView data={{ nodeType: "CalculatorNode" }} />}
        onSelectingBlock={() => handleItemClick("CalculatorNode")}
        tooltipCondition={!isCalculatorsLoading && calculators?.data?.results?.length === 0}
        tooltipText={t("calculatorNode")}
        isNodeDisabled={isCalculatorsLoading}
      />

      {isEnvVariableTruthy(String(getEnvVar("WORKFLOW_LOOPS_ENABLED"))) && (
        <Fragment>
          <Box mt={1} />
          <MenuItemWithTooltip
            Item={<LoopStartNode menuView />}
            onSelectingBlock={() => handleItemClick("LoopStartNode")}
            tooltipCondition={isNodeInLoop(source, getNodes()).isNodeInNestedLoop}
            tooltipText={t("nestedLoops")}
            isNodeDisabled={false}
          />
        </Fragment>
      )}

      <Box mt={1} />
      {/* Script node */}
      <MenuItemWithTooltip
        Item={<ScriptNode menuView data={{ nodeType: "ScriptNode", scriptType: "custom" }} />}
        onSelectingBlock={() => handleItemClick("ScriptNode")}
        tooltipCondition={!isScriptsLoading && scripts?.data?.results?.length === 0}
        tooltipText={t("scriptNode")}
        isNodeDisabled={isScriptsLoading}
      />

      <MenuItemWithTooltip
        Item={<ScriptNode menuView data={{ nodeType: "ScriptNode", scriptType: "marketplace" }} />}
        onSelectingBlock={() => handleItemClick("ScriptNode", { scriptType: "marketplace" })}
        tooltipCondition={!isScriptsLoading && scripts?.data?.results?.length === 0}
        tooltipText={
          "This project doesn't have any integrations, therefore you can't add an integration node in a workflow"
        }
        isNodeDisabled={isScriptsLoading}
      />

      <Box mt={1} />
      {/* Project node */}
      <MenuItemWithTooltip
        Item={<ProjectNode menuView data={{ nodeType: "ProjectNode" }} />}
        onSelectingBlock={() => handleItemClick("ProjectNode")}
        tooltipCondition={!isProjectsLoading && projects?.data?.count === 0}
        tooltipText={t("projectNode")}
        isNodeDisabled={isProjectsLoading}
      />

      {isNodeInLoop(source, getNodes()).isNodeInLoop && (
        <div style={{ marginTop: "8px" }}>
          <MenuItemWithTooltip
            Item={<LabeledNode menuView data={{ nodeType: "EndResultNode", isNodeInLoop: true, updateGraph }} />}
            onSelectingBlock={() => handleItemClick("EndResultNode")}
            tooltipCondition={validateAddingLabelNode()}
            tooltipText={t("endResultNode")}
            isNodeDisabled={false}
          />
        </div>
      )}
    </Menu>
  )
}

/**
 * CustomEdgeLabel component that renders edge labels and add node buttons for workflow edges.
 * Provides interactive elements for adding nodes between existing workflow nodes.
 */
export const CustomEdgeLabel: FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  source,
  target,
  style,
}) => {
  // React Flow hooks
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })
  const store = useStoreApi()
  const { getNodes } = store.getState()

  // Theme hook
  const theme = getTheme()

  // State hooks
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement | undefined>(null)

  // Refs
  const buttonRef = useRef<HTMLButtonElement>()

  // Constants
  const updateGraph = data?.updateGraph ?? null

  // Memoized values
  const sourceNodeType = useMemo(() => {
    const sourceNode = getNodes()?.find((n) => n.id === source)
    if (sourceNode) {
      return sourceNode?.data?.nodeType
    }
  }, [getNodes, source])

  const targetNodeType = useMemo(() => {
    const targetNode = getNodes()?.find((n) => n.id === target)
    if (targetNode) {
      return targetNode?.type
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNodes(), target])

  // Computed values
  // Boolean (showAddNodeButton) if these conditions met
  const showAddNodeButton = Boolean(
    !data?.readMode && sourceNodeType !== "EndResultNode" && targetNodeType !== "AddBlockNode",
  )

  // Boolean (center label) if these conditions met
  const centerLabel = Boolean(
    data?.readMode || NODES_WITHOUT_LABEL.includes(sourceNodeType) || targetNodeType === "AddBlockNode",
  )

  const adjustedFilterXCoordinates = Boolean(sourceNodeType === "FilterNode" && !data?.readMode && showAddNodeButton)

  // Helper functions
  const getAddNodeButtonXAxis = (): string => {
    return sourceNodeType === "FilterNode" && data?.text === "false"
      ? "-40%"
      : sourceNodeType === "FilterNode" && data?.text === "true"
        ? "-50%"
        : "-50%"
  }

  const getAddNodeButtonYAxis = (): string => {
    return sourceNodeType === "FilterNode" ? "-20%" : NODES_WITHOUT_LABEL.includes(sourceNodeType) ? "-50%" : "10%"
  }

  return (
    <Fragment>
      <NodeOptionsMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        source={source}
        updateGraph={updateGraph}
        target={target}
        edgeLabel={data?.text}
      />

      <path id={id} className="react-flow__edge-path" d={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        {!NODES_WITHOUT_LABEL.includes(sourceNodeType) && (
          <div
            style={{
              position: "absolute",
              transform: adjustedFilterXCoordinates
                ? `translate(-50%, 40%) translate(${sourceX}px,${sourceY}px)`
                : `translate(-50%, ${
                    centerLabel ? "-50%" : sourceNodeType === "FilterNode" ? "-85%" : "-120%"
                  }) translate(${labelX}px,${labelY}px)`,

              background: theme.palette.base.background[1],
              padding: "6px 12px",
              borderRadius: 2,
              fontSize: 12,
              fontWeight: 700,
              color: theme.palette.base.text[1],
            }}
            className="nodrag nopan"
          >
            <Typography variant="label-bold">{data.text}</Typography>
          </div>
        )}

        {showAddNodeButton && (
          <div
            style={{
              position: "absolute",
              transform: `translate(${getAddNodeButtonXAxis()}, ${getAddNodeButtonYAxis()}) translate(${labelX}px,${labelY}px)`,
              fontSize: 12,
              // everything inside EdgeLabelRenderer has no pointer events by default
              // if you have an interactive element, set pointer-events: all
              pointerEvents: "all",
            }}
            className="nodrag nopan"
          >
            <Button size="large" variant="secondary" onClick={() => setAnchorEl(buttonRef.current)} ref={buttonRef}>
              <AddIcon fontSize="small" />
            </Button>
          </div>
        )}
      </EdgeLabelRenderer>
    </Fragment>
  )
}
